// --- FINAL, CORRECTED & UPGRADED FILE: packages/components/src/admin/tasks/DraggableTaskCard.tsx ---

import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { GripVertical, Paperclip, MessageSquare, Star } from 'lucide-react';
import clsx from 'clsx';

import { Badge } from '@dopomogai/ui';
import type { ProjectTask } from '@dopomogai/types';

interface DraggableTaskCardProps {
  /** The full task data object to display. */
  task: ProjectTask;
  /** True if the card is being rendered as a drag overlay, which applies a different style. */
  isDragging?: boolean;
  /** 
   * A callback function that fires when the card is clicked. 
   * The parent component decides what this action does (e.g., expand inline).
   */
  onExpand: () => void;
}

const taskTypeColors: { [key in ProjectTask['task_type']]?: 'blue' | 'purple' | 'green' | 'yellow' | 'red' | 'gray' } = {
    feature: 'blue',
    bugfix: 'red',
    chore: 'purple',
    docs: 'green',
    research: 'yellow',
    operations: 'gray',
    sales: 'gray',
    marketing: 'gray',
};

/**
 * A compact, draggable card representing a single task. It's designed to be
 * visually consistent with the Neobrutalist design system and functionally
 * flexible for use in various planning boards.
 */
export const DraggableTaskCard: React.FC<DraggableTaskCardProps> = ({ task, isDragging = false, onExpand }) => {
  const { t } = useTranslation('admin_tasks');

  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: task._id,
    data: {
      task, // Pass the full task object in the drag data for the drop handler
      parent: task.sprint_id || 'backlog',
    },
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        // When dragging, we want it to be above other elements
        zIndex: 100, 
      }
    : undefined;
    
  const handleCardClick = (e: React.MouseEvent) => {
    // Stop propagation to prevent any other parent handlers from firing.
    e.stopPropagation();
    // dnd-kit is smart enough to prevent this from firing on a successful drag,
    // so it only fires on a clean click.
    onExpand(); 
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onExpand();
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      // Listeners are on the main div, making the whole card draggable.
      {...listeners}
      {...attributes}
      // Make the card behave like a button for clicks and keyboard interaction.
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={t('expandTaskAriaLabel', { taskTitle: task.title })}
      className={clsx(
        "bg-white p-3 rounded-lg border-2 border-black flex items-center gap-3",
        "relative", // Needed for z-index to work
        // --- DESIGN SYSTEM FIX: Apply the "Lift & Shift" effect ---
        !isDragging && "cursor-grab transition-all duration-200 transform",
        !isDragging && "hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgb(17,24,39)]",
        !isDragging && "active:translate-x-0 active:translate-y-0 active:shadow-none active:cursor-grabbing",
        // Style for the overlay ghost when dragging
        isDragging && "shadow-xl ring-2 ring-emerald-500 cursor-grabbing"
      )}
    >
        {/* Drag Handle Icon - now purely visual and better aligned */}
        <div className="text-slate-400 flex-shrink-0 pt-0.5">
            <GripVertical size={18} />
        </div>
        
        {/* Main Content Area */}
        <div className="flex-grow min-w-0">
            {/* Title with two-line wrap */}
            <p 
                className="font-bold text-slate-800 leading-snug line-clamp-2" 
                title={task.title}
                // Ensures consistent height whether title is one or two lines
                style={{ minHeight: '2.5rem' }} 
            >
                {task.title}
            </p>
            {/* Lower row with stats */}
            <div className="flex items-center gap-4 text-xs text-slate-500 font-medium mt-1.5">
                {task.points != null && ( // Check for null or undefined
                    <span className="flex items-center gap-1 font-bold text-slate-700" title={`${task.points} points`}>
                        <Star size={12} /> {task.points}
                    </span>
                )}
                {task.links && task.links.length > 0 && (
                    <span className="flex items-center gap-1" title={`${task.links.length} links`}>
                        <Paperclip size={12} /> {task.links.length}
                    </span>
                )}
                {/* Placeholder for comments count */}
                <span className="flex items-center gap-1" title="0 comments">
                    <MessageSquare size={12} /> 0
                </span>
            </div>
        </div>
        
        {/* Badge on the far right */}
        <div className="ml-auto flex-shrink-0 pl-2">
            <Badge text={task.task_type} accentColor={taskTypeColors[task.task_type] || 'gray'} />
        </div>
    </div>
  );
};